import {Component, OnInit} from '@angular/core';
import {ComplaintService} from '../../data/services/complaint.service';
import {HupSubscriptionsService} from '../../data/services/hup-subscriptions.service';
import {TranslationService} from '../../core/services/translation.service';
import {Complaint} from '../../data/models/complaint.model';
import {HupSubscription} from '../../data/models/subscription.model';
import {ActivatedRoute} from '@angular/router';
import {BaseDataService} from '../../core/services/base-data.service';
import {NavigationCommand, NavigationService} from "../../data/services/navigation.service";
import {ConfigService} from "../../core/services/config.service";
import {BaseData} from "../../data/models/base-data.model";
import {isDigital} from "../../core/utils/offer-utils";

@Component({
  selector: 'app-complaint',
  templateUrl: './complaint.component.html',
  styleUrls: ['./complaint.component.scss']
})
export class ComplaintComponent implements OnInit {
  isLoadingComp: boolean = false;
  isLoadingSub: boolean = false;
  complaints: Complaint[] = [];
  complaintsGrouped: Complaint[][] = [];
  subs: HupSubscription[] = [];
  errorsArr: string[] = [];
  isDigitalOrderAllowed: boolean = true;

  constructor(private complaintService: ComplaintService,
              private hupSubscriptionsService: HupSubscriptionsService,
              private navigationService: NavigationService,
              private route: ActivatedRoute,
              public translationService: TranslationService,
              private baseDataService: BaseDataService,
              private configService: ConfigService
  ) {
    this.isDigitalOrderAllowed = configService.getConfig('complaint.show.digital.orders')?.value === '1';
  }

  ngOnInit(): void {
    this.isLoadingComp = true;
    this.isLoadingSub = true;
    this.complaintService.getComplaints().subscribe(complaints => {
      this.isLoadingComp = false;
      complaints.forEach((comp: Complaint) => {
        this.baseDataService.handleComplaint(comp);
        let isGrouped = false;
        this.complaintsGrouped.forEach((compGroup: Complaint[]) => {
          if (!isGrouped && compGroup.length > 0) {
            let compToCompare = compGroup.at(0);
            isGrouped = compToCompare.subId == comp.subId && compToCompare.memo.memoText == comp.memo.memoText &&
              compToCompare.reason == comp.reason;
            const actionsForWebConfig = this.configService.getConfig('complaint.webFlags');
            const actionsForWeb = actionsForWebConfig?.value.split(',');
            const compensations:BaseData[] = [];
            const compGroupCompensations:BaseData[] = [];
            comp.compensations.forEach(compensation => {
              if (actionsForWeb.includes(compensation.key) || actionsForWeb.includes(compensation['code'])) {
                compensations.push(compensation);
              }
            });
            compGroup.at(0).compensations.forEach(compensation => {
              if (actionsForWeb.includes(compensation.key) || actionsForWeb.includes(compensation['code'])) {
                compGroupCompensations.push(compensation);
              }
            });
            comp.compensations = compensations;
            compGroup.at(0).compensations = compGroupCompensations;
            if (isGrouped) {
              this.complaintsGrouped.push(new Array(comp));
              this.complaintsGrouped.sort((a, b) => {
                return a.at(0).validDate.validFrom > b.at(0).validDate.validFrom ? -1 : 1;
              });
            }
          }
        });
        if (!isGrouped) {
          this.complaintsGrouped.push(new Array(comp));
          this.complaintsGrouped.sort((a, b) => {
            return a.at(0).validDate.validFrom > b.at(0).validDate.validFrom ? -1 : 1;
          });
        }
      });
      this.complaints = complaints;
    }, error => {
      this.isLoadingComp = false;
      this.errorsArr.push(this.translationService.getTranslation('error_complaints_init'));
    });

    this.hupSubscriptionsService.getSubscriptions().subscribe(subs => {
      this.isLoadingSub = false;
      this.subs = [];

      subs.forEach(sub => {
        if (
          (sub.validDate.validUntil == null || sub.validDate.validUntil > new Date()) &&
          sub.backendId > 0 &&
          // Wenn es ein digitales Abo (ePaper/PaidContent) ist, muss auch die Vorgabe aktiv sein!
          ((isDigital(sub.offer?.offerType) && this.isDigitalOrderAllowed ) || !isDigital(sub.offer?.offerType))
        ) {
          this.subs.push(sub);
        }
      });

      if (this.subs.length === 1 && this.subs[0]) {
        this.onNewComplaint(this.subs[0]);
      }
    }, error => {
      this.isLoadingSub = false;
      this.errorsArr.push(this.translationService.getTranslation('error_subscription_init'));
    });

    this.complaintService.consumeErrorMessages().subscribe(
      msgs => msgs.forEach(
        msg => this.errorsArr.push(msg)
      )
    );
  }

  onNewComplaint(sub: HupSubscription): void {
    this.navigationService.navigateTo(NavigationCommand.COMPLAINTNEW, [sub.webId + '', sub.backendId + '', 'false', 'true'])
  }

  onDetails(index: number): void {
    let isPrint = true;
    this.subs.forEach(sub => {
      const complaint = this.complaints[index];
      if (sub.backendId === complaint.subId) {
        isPrint = !isDigital(sub.offer?.offerType);
      }
    });
    this.navigationService.navigateTo(NavigationCommand.COMPLAINTEDIT, [this.complaints[index].webId + '', this.complaints[index].backendId + '', 'false', isPrint ? 'true' : 'false']);

  }

  onDelete(index: number): void {
    alert('TODO: löschen');
  }

  onEdit(index: number): void {
    let isPrint = true;
    this.subs.forEach(sub => {
      const complaint = this.complaints[index];
      if (sub.backendId === complaint.subId) {
        isPrint = !isDigital(sub.offer?.offerType);
      }
    });
    this.navigationService.navigateTo(NavigationCommand.COMPLAINTEDIT, [this.complaints[index].webId + '', this.complaints[index].backendId + '', 'false', isPrint ? 'true' : 'false']);
  }

  /**
   * Das Abo-Startdatum wird ausgewertet:
   * beginnt das Abo nach heute, ist keine Reklamation möglich.
   * @param subscription - Abonnement, welches überprüft werden soll.
   * @return boolean-Wert: true - Reklamation möglich,
   * false - Reklamation ist nicht möglich
   */
  isComplainable(subscription: HupSubscription): boolean {
    const startDate = subscription.validDate.validFrom;
    const today = new Date();

    startDate.setHours(0,0,0,0);
    today.setHours(0,0,0,0);

    return startDate.getTime() <= today.getTime();
  }

  protected readonly TextDecoderStream = TextDecoderStream;
}
